import { useEffect } from 'react';
import { FullStory, isInitialized } from '@fullstory/browser';
import { FullStorySession, setFullStorySessionProvider } from './fullStorySession';

/**
 * Parse a FullStory replay URL into org id, Client ID, and replay position.
 * Example: https://app.fullstory.com/ui/o-1VHTDK-na1/client-session/bc94a237-...:65007796-...:1789057311531
 */
export function parseFullStorySession(sessionUrl: string): FullStorySession | undefined {
  const match = sessionUrl.match(/\/(?:client-)?session\/([^/?#]+)/);
  if (!match?.[1]) return undefined;

  const orgId = sessionUrl.match(/\/ui\/([^/?#]+)\//)?.[1];

  let decoded: string;
  try {
    decoded = decodeURIComponent(match[1]);
  } catch {
    return undefined;
  }

  const parts = decoded.split(':');
  if (parts.length < 2 || parts.length > 3 || parts.some((part) => !part)) return undefined;

  const clientId = `${parts[0]}:${parts[1]}`;
  const timestampMs = parts[2];

  const session: FullStorySession = { clientId };
  if (orgId) session.orgId = orgId;
  if (timestampMs !== undefined && /^\d+$/.test(timestampMs)) session.timestampMs = timestampMs;

  return session;
}

/**
 * Register the FullStory session resolver with baggageRegistry for the life of the component.
 * Lazily resolves the session so ui_rum_session_ts reflects the user's position at request time.
 */
export function useFullStorySession(): void {
  useEffect(() => {
    setFullStorySessionProvider(() => {
      if (!isInitialized()) return undefined;

      try {
        const url = FullStory('getSession', { format: 'url.now' });
        return url ? parseFullStorySession(String(url)) : undefined;
      } catch {
        return undefined;
      }
    });

    return () => {
      setFullStorySessionProvider(undefined);
    };
  }, []);
}
